
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import SvgIcon from "@jamescoyle/vue-icon";
import SearchRow from "@/components/generic/search-row.vue";
import { mdiTimerSand, mdiCursorDefaultClickOutline, mdiKey, mdiKeyChange, mdiDoorOpen } from "@mdi/js";
import KeyStatus from "@/components/keys/KeyStatus.vue";
import { ROWS_PER_PAGE } from "@/utils/constants";

@Component({
  components: { KeyStatus, SvgIcon, SearchRow },
})
export default class KeyAccess extends Vue {

  @Getter private currentUser: any;
  @Getter private envCompanyId: number | null;
  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private isMobile: boolean;

  private tab: string = "key-access";
  private page: number = 1;
  private itemsPerPage: number = 10;
  private search: string = "";
  private loading: boolean = false;
  private now: Date = new Date();

  private keyAccessList: any[] = [];
  private companiesList: any[] = [];
  private totalItem: number = 0;

  private displayCompanyFilter: boolean = false;
  private selectedCompanyId: number | null = null;

  /**
   * ICON
   */
  private mdiTimerSand: any = mdiTimerSand;
  private mdiCursorDefaultClickOutline: any = mdiCursorDefaultClickOutline;
  private mdiKeyChange: any = mdiKeyChange;

  private headers: object[] = [
    { text: this.$t("KeyAccess.countAccessLeft"), value: "countAccessLeft", sortable: false },
    { text: this.$t("KeyAccess.startDate"), value: "startDate", sortable: false },
    { text: this.$t("KeyAccess.endDate"), value: "endDate", sortable: false },
    { text: this.$t("KeyAccess.building"), value: "building", sortable: false },
    { text: "", value: "objectType", sortable: false },
    { text: this.$t("KeyAccess.objects"), value: "keys", sortable: false },
    { text: "", value: "status", sortable: false },

  ];

  private dataTableOptions: any = {};

  private ROWS_PER_PAGE = ROWS_PER_PAGE;

  /**
   * Handles options for the data table.
   *
   * @param {Object} event - The event object containing the page and itemsPerPage values.
   * @return {void}
   */
  private dataTableHandleOptions(event) {
    this.page = event.page;
    this.itemsPerPage = event.itemsPerPage;
    this.getKeyAccessList();
  }

  private mounted(): void {
    if (this.isMobile) {
      this.getKeyAccessList();
      this.initScroll();
    }
    this.getCompanies();
  }

  private initScroll(): void {
    window.addEventListener("scroll", async (): Promise<void> => {
      const listElm = document.querySelector("#access-list");
      const currentScrollValue: number = document.documentElement.scrollTop;
      const threshold: number = listElm?.clientHeight - window.innerHeight - 1000;
      const isTargetPos: boolean = currentScrollValue > threshold;
      if (isTargetPos && !this.loading && !(this.totalItem === this.keyAccessList?.length)) {
        this.page++;
        await this.getKeyAccessList();
      }
    });
  }

  /**
   * Retrieves the key access list for the current user.
   *
   * @private
   * @returns {Promise<void>} A promise that resolves when the key access list is retrieved successfully.
   */
  private getKeyAccessList(overwrite = false) {
    this.loading = true;
    return this.$store.dispatch("users/getUserKeyAccess", {
      userId: this.currentUser.id,
      search: this.search || null,
      page: this.page,
      itemsPerPage: this.itemsPerPage,
      companyId: this.selectedCompanyId,
    })
      .then((data) => {
        if (this.isMobile && overwrite === false) {
          this.keyAccessList = this.keyAccessList.concat(data.rows);
        } else {
          this.keyAccessList = data.rows;
        }
        this.totalItem = data.count;
      })
      .finally(() => { this.loading = false; });
  }

  private getCompanies() {
    return this.$store.dispatch("users/getUserCompanies", {
      userId: this.currentUser.id,
    }).then((data) => {
      this.companiesList = data;
    });
  }

  /**
   * Handles the search operation.
   *
   * @return {void}
   */
  private searchHandler(): void {
    this.page = 1;
    const OVERWRITE = true;
    this.getKeyAccessList(OVERWRITE);
  }

  /**
   * Triggers the search handler method when the enter key is pressed.
   *
   * @param {KeyboardEvent} e - The keyboard event object.
   * @return {void}
   */
  private enterKeyPress(e) {
    if (e.keyCode === 13) {
      this.searchHandler();
    }
  }

  /**
   * Returns the status icon based on the item's dates.
   *
   * @param {Object} item - The item to check the dates.
   * @return {string} The status icon.
   */
  private getStatusIcon(item) {
    if (new Date(item.startDate) < this.now && (!item.endDate || new Date(item.endDate) > this.now)) {
      return null;
    } else {
      return this.mdiTimerSand;
    }
  }

  /**
   * Returns the object type icon based on the item's infos.
   *
   * @param {Object} item - The item to check.
   * @return {string} The status icon.
   */
  private getObjectTypeIcon(item): string {
    switch (item.type) {
      case "keys":
        return mdiKeyChange;
      case "key":
        return mdiKey;
      case "nonTransferable":
        return mdiDoorOpen;
      default:
        return "";
    }
  }

  /**
   * This method is used to set the selected key and navigate to the specified route.
   *
   * @private
   * @param {Object} key - The key object to be selected.
   * @returns {void}
   */
  private selectKey(key) {
    return this.$router.push({ name: "key", params: { id: key.id } });
  }

  /**
   * This method is used to set the selected nonTransferable and navigate to the specified route.
   *
   * @private
   * @param {Object} nonTransferable - nonTransferable key object to be selected.
   * @returns {void}
   */
  private selectNonTransferable(nonTransferable) {
    return this.$router.push({ name: "non-transferable", params: { id: nonTransferable.id } });
  }

  private getKeyAccessBuilding(keyAccess) {
    return keyAccess.KeyGroup.Building
      || keyAccess.KeyGroup.Keys?.[0]?.Building
      || keyAccess.KeyGroup.NonTransferables?.[0]?.Building;
  }

}
